<template>
  <v-container fluid>
    <v-row align="center">
      <v-col class="d-flex flex-row align-center flex-wrap">
        <v-btn text class="mr-4" @click="$router.go(-1)"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <h1>Report Builder</h1>
        <v-progress-circular indeterminate color="green" v-if="loading" class="ml-3" />
        <v-spacer />
        <v-btn outlined class="mr-2" to="/report-hub"><v-icon left>mdi-chart-box-outline</v-icon>Open Reports Hub</v-btn>
        <v-btn outlined class="mr-2" :loading="busy.exportAll" @click="exportAll"><v-icon left>mdi-export</v-icon>Export all</v-btn>
        <v-btn outlined class="mr-2" @click="openCategories"><v-icon left>mdi-folder-outline</v-icon>Categories</v-btn>
        <v-btn outlined class="mr-2" @click="importDialog = true"><v-icon left>mdi-import</v-icon>Import</v-btn>
        <v-btn color="primary" to="/superadmin/report-builder/new"><v-icon left>mdi-plus</v-icon>New report</v-btn>
      </v-col>
    </v-row>

    <div class="d-flex align-center flex-wrap mb-2">
      <v-text-field v-model="search" dense outlined hide-details clearable label="Filter" prepend-inner-icon="mdi-magnify" style="max-width: 320px" />
      <span class="ml-4 text-caption grey--text">
        <v-icon x-small>mdi-drag</v-icon> Drag a report to reorder it or drop it in another category.
        <span v-if="search" class="warning--text">Clear the filter to drag.</span>
      </span>
      <v-spacer />
      <span class="text-caption grey--text">{{ totalCount }} reports - {{ publishedCount }} published</span>
    </div>

    <!-- bulk actions for the ticked reports -->
    <v-slide-y-transition>
      <v-card v-if="selected.length" outlined color="primary lighten-5" class="mb-3" style="position: sticky; top: 56px; z-index: 5">
        <v-card-text class="py-2 d-flex align-center flex-wrap">
          <b class="mr-4">{{ selected.length }} selected</b>
          <v-btn small text color="success" @click="bulk({ visibility: 'published' })"><v-icon left small>mdi-eye-outline</v-icon>Publish</v-btn>
          <v-btn small text @click="bulk({ visibility: 'draft' })"><v-icon left small>mdi-file-edit-outline</v-icon>Make draft</v-btn>
          <v-btn small text @click="bulk({ visibility: 'hidden' })"><v-icon left small>mdi-eye-off-outline</v-icon>Hide</v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on }"><v-btn small text v-on="on"><v-icon left small>mdi-folder-move-outline</v-icon>Move to category</v-btn></template>
            <v-list dense>
              <v-list-item v-for="c in board.filter((x) => x.id)" :key="c.id" @click="bulk({ categoryId: c.id })"><v-list-item-title>{{ c.name }}</v-list-item-title></v-list-item>
            </v-list>
          </v-menu>
          <v-btn small text @click="openAccess"><v-icon left small>mdi-account-lock-outline</v-icon>Who can open them...</v-btn>
          <v-spacer />
          <v-btn small text @click="selected = []">Clear selection</v-btn>
        </v-card-text>
      </v-card>
    </v-slide-y-transition>

    <!-- one card per category; reports can be dragged within and between them -->
    <v-card v-for="cat in board" :key="cat.id || 'none'" outlined class="mb-4">
      <v-card-title class="py-2 d-flex align-center flex-wrap">
        <v-checkbox
          :input-value="allSelected(cat)" :indeterminate="someSelected(cat)" dense hide-details class="mt-0 pt-0 mr-2"
          title="Select every report in this category" @change="toggleCategory(cat, $event)"
        />
        <v-icon v-if="cat.icon" class="mr-2">{{ cat.icon }}</v-icon>
        <span class="text-subtitle-1">{{ cat.name }}</span>
        <span class="ml-2 text-caption grey--text">{{ cat.reports.length }} reports - {{ publishedIn(cat) }} published</span>
        <v-spacer />
        <v-btn small text color="success" :disabled="!cat.reports.length" @click="bulkCategory(cat, { visibility: 'published' })"><v-icon left small>mdi-eye-outline</v-icon>Publish all</v-btn>
        <v-btn small text :disabled="!cat.reports.length" @click="bulkCategory(cat, { visibility: 'draft' })"><v-icon left small>mdi-file-edit-outline</v-icon>All to draft</v-btn>
        <v-btn small text :disabled="!cat.reports.length" @click="bulkCategory(cat, { visibility: 'hidden' })"><v-icon left small>mdi-eye-off-outline</v-icon>Hide all</v-btn>
        <v-btn small text :disabled="!cat.reports.length" @click="selectCategoryAccess(cat)"><v-icon left small>mdi-account-lock-outline</v-icon>Who can open...</v-btn>
      </v-card-title>
      <v-divider />
      <draggable
        v-model="cat.reports" group="reports" handle=".rb-row-handle" :disabled="!!search || !cat.id" ghost-class="rb-ghost"
        class="rb-dropzone" @change="onChange(cat, $event)"
      >
        <div
          v-for="r in cat.reports" :key="r.id" v-show="matches(r)"
          class="d-flex align-center px-2 py-1 rb-row" :class="{ 'rb-row-sel': selected.includes(r.id) }"
        >
          <v-icon class="rb-row-handle mr-1" small style="cursor: grab">mdi-drag</v-icon>
          <v-checkbox v-model="selected" :value="r.id" dense hide-details class="mt-0 pt-0 mr-2" />
          <div class="flex-grow-1" style="min-width: 0">
            <router-link :to="`/superadmin/report-builder/edit/${r.id}`">{{ r.name }}</router-link>
            <div class="caption grey--text text-truncate">{{ r.key }}</div>
          </div>
          <v-chip x-small class="mx-2" :color="chipColor(r.visibility)" :text-color="r.visibility === 'published' ? 'white' : undefined">{{ r.visibility }}</v-chip>
          <span class="caption grey--text mr-2" style="width: 34px">v{{ r.version }}</span>
          <v-icon v-if="!r.verification" small class="mr-2" title="Tests not run">mdi-help-circle-outline</v-icon>
          <v-icon v-else-if="r.verification.ok" small color="success" class="mr-2" :title="`${r.verification.passed}/${r.verification.total} passed`">mdi-check-decagram</v-icon>
          <v-icon v-else small color="error" class="mr-2" :title="`${r.verification.passed}/${r.verification.total} passed`">mdi-alert-circle</v-icon>
          <v-btn icon x-small title="Edit" :to="`/superadmin/report-builder/edit/${r.id}`"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
          <v-btn icon x-small title="Open in Reports Hub" :to="`/report-hub/${r.key}`"><v-icon small>mdi-eye-arrow-right-outline</v-icon></v-btn>
          <v-btn icon x-small title="Duplicate" @click="duplicate(r)"><v-icon small>mdi-content-copy</v-icon></v-btn>
          <v-btn icon x-small title="Run tests" :loading="busy['t' + r.id]" @click="runTests(r)"><v-icon small>mdi-test-tube</v-icon></v-btn>
          <v-btn icon x-small :title="r.visibility === 'published' ? 'Unpublish (make draft)' : 'Publish'" @click="bulkIds([r.id], { visibility: r.visibility === 'published' ? 'draft' : 'published' })">
            <v-icon small>{{ r.visibility === 'published' ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
          </v-btn>
          <v-btn icon x-small title="Export" @click="exportOne(r)"><v-icon small>mdi-download</v-icon></v-btn>
          <v-btn icon x-small title="Delete" @click="confirmDelete = r"><v-icon small>mdi-delete-outline</v-icon></v-btn>
        </div>
        <div v-if="!cat.reports.length" class="text-caption grey--text pa-3">No reports - drop one here.</div>
      </draggable>
    </v-card>

    <!-- who can open: replace / add / remove user types for the selection -->
    <v-dialog v-model="accessDialog" max-width="520">
      <v-card>
        <v-card-title>Who can open {{ selected.length }} report{{ selected.length === 1 ? '' : 's' }}</v-card-title>
        <v-card-text>
          <v-radio-group v-model="access.mode" class="mt-0">
            <v-radio value="replace" label="Replace - set exactly these user types (empty = everyone with report access)" />
            <v-radio value="add" label="Add these user types to what each report already allows" />
            <v-radio value="remove" label="Remove these user types from each report" />
          </v-radio-group>
          <v-select
            v-model="access.userTypeIds" :items="userTypes" item-text="name" item-value="id" multiple chips small-chips deletable-chips
            outlined dense label="User types"
          />
          <div class="text-caption grey--text">Each report keeps its own version history, so a change can be undone from the report's Versions tab.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="accessDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="busy.access" :disabled="access.mode !== 'replace' && !access.userTypeIds.length" @click="applyAccess">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- test results -->
    <v-dialog v-model="testDialog" max-width="760">
      <v-card v-if="testResult">
        <v-card-title>
          {{ testResult.name }}
          <v-chip small class="ml-3" :color="testResult.data.summary.ok ? 'success' : 'error'">
            {{ testResult.data.summary.passed }}/{{ testResult.data.summary.total }} passed
          </v-chip>
        </v-card-title>
        <v-card-text>
          <div v-for="group in testGroups" :key="group.title" class="mb-3">
            <div class="text-subtitle-2">{{ group.title }}</div>
            <div v-if="!group.items.length" class="caption grey--text">None defined.</div>
            <div v-for="(t, i) in group.items" :key="i" class="d-flex align-start my-1">
              <v-icon small :color="t.ok ? 'success' : 'error'" class="mr-2 mt-1">{{ t.ok ? 'mdi-check' : 'mdi-close' }}</v-icon>
              <div>
                <div>{{ t.name }}</div>
                <div v-if="t.description" class="caption grey--text">{{ t.description }}</div>
                <div v-for="(p, j) in t.problems || []" :key="'p' + j" class="caption error--text">{{ p }}</div>
                <div v-if="t.error" class="caption error--text">{{ t.error }}</div>
                <div v-if="t.violations" class="caption error--text">
                  {{ t.violations }} violation(s), e.g. {{ JSON.stringify(t.sample && t.sample[0]) }}
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="testDialog = false">Close</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- categories: order, names, icons -->
    <v-dialog v-model="catDialog" max-width="560">
      <v-card>
        <v-card-title>Categories</v-card-title>
        <v-card-text>
          <div class="text-caption grey--text mb-2">Drag to set the order they appear in the Reports Hub. Categories are created automatically when a report uses a new name.</div>
          <draggable v-model="cats" handle=".rb-cat-handle" @end="saveCatOrder">
            <v-row v-for="c in cats" :key="c.id" dense align="center" class="mb-1">
              <v-col cols="auto"><v-icon class="rb-cat-handle" style="cursor: grab">mdi-drag</v-icon></v-col>
              <v-col cols="5"><v-text-field v-model="c.name" outlined dense hide-details label="Name" @change="saveCat(c)" /></v-col>
              <v-col cols="4"><v-text-field v-model="c.icon" outlined dense hide-details label="Icon (mdi-...)" @change="saveCat(c)" /></v-col>
              <v-col cols="auto"><v-btn icon small :disabled="count(c) > 0" :title="count(c) ? 'Move its reports first' : 'Delete'" @click="deleteCat(c)"><v-icon small>mdi-delete-outline</v-icon></v-btn></v-col>
            </v-row>
          </draggable>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="catDialog = false">Close</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <!-- import -->
    <v-dialog v-model="importDialog" max-width="520">
      <v-card>
        <v-card-title>Import reports</v-card-title>
        <v-card-text>
          <v-file-input v-model="importFile" accept=".json,.rbreport.json" label="Report export file (.rbreport.json)" outlined dense />
          <v-select
            v-model="importStrategy" outlined dense label="If a report with the same key exists"
            :items="[{ text: 'Skip it', value: 'skip' }, { text: 'Overwrite (saves a new version)', value: 'overwrite' }, { text: 'Import as a copy', value: 'duplicate' }]"
          />
          <div v-for="(r, i) in importResults" :key="i" class="caption">
            <b>{{ r.status }}</b> {{ r.key }} <span v-if="r.error" class="error--text">- {{ r.error }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="importDialog = false">Close</v-btn>
          <v-btn color="primary" :disabled="!importFile" :loading="busy.import" @click="doImport">Import</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- delete -->
    <v-dialog :value="!!confirmDelete" max-width="420" @input="confirmDelete = null">
      <v-card v-if="confirmDelete">
        <v-card-title>Delete report?</v-card-title>
        <v-card-text>"{{ confirmDelete.name }}" will be removed from the Reports Hub. Its saved versions are kept.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDelete = null">Cancel</v-btn>
          <v-btn color="error" :loading="busy.delete" @click="doDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import draggable from 'vuedraggable'

function saveJson (filename, data) {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(a.href), 2000)
}

export default {
  name: 'ReportBuilderList',
  components: { draggable },
  data () {
    return {
      loading: false,
      busy: {},
      search: '',
      board: [], // [{ id, name, icon, reports: [...] }] in display order
      reports: [],
      selected: [],
      userTypes: [],
      accessDialog: false,
      access: { mode: 'replace', userTypeIds: [] },
      testDialog: false,
      testResult: null,
      importDialog: false,
      importFile: null,
      importStrategy: 'skip',
      importResults: [],
      confirmDelete: null,
      catDialog: false,
      cats: []
    }
  },
  computed: {
    ...mapGetters(['getEndpoint']),
    base () { return `${this.getEndpoint}/api/rb-builder` },
    totalCount () { return this.reports.length },
    publishedCount () { return this.reports.filter((r) => r.visibility === 'published').length },
    testGroups () {
      const d = this.testResult.data
      return [
        { title: 'Fixture cases', items: d.fixture.cases },
        { title: 'Reconciliations on fixture data', items: d.fixture.reconciliations },
        { title: 'Reconciliations on live data', items: d.live }
      ]
    }
  },
  async mounted () {
    await this.load()
    try {
      const meta = await axios.get(`${this.base}/meta`)
      this.userTypes = meta.data.data.userTypes
    } catch (e) { /* the access dialog just stays empty */ }
  },
  methods: {
    chipColor (v) { return v === 'published' ? 'success' : v === 'hidden' ? 'grey' : 'warning' },
    matches (r) {
      const q = (this.search || '').trim().toLowerCase()
      return !q || [r.name, r.key, r.description].join(' ').toLowerCase().includes(q)
    },
    count (c) { return this.reports.filter((r) => r.categoryId === c.id).length },
    publishedIn (cat) { return cat.reports.filter((r) => r.visibility === 'published').length },

    // ---- selection ----
    allSelected (cat) { return cat.reports.length > 0 && cat.reports.every((r) => this.selected.includes(r.id)) },
    someSelected (cat) { return !this.allSelected(cat) && cat.reports.some((r) => this.selected.includes(r.id)) },
    toggleCategory (cat, on) {
      const ids = cat.reports.map((r) => r.id)
      this.selected = on ? [...new Set([...this.selected, ...ids])] : this.selected.filter((id) => !ids.includes(id))
    },
    selectCategoryAccess (cat) {
      this.selected = cat.reports.map((r) => r.id)
      this.openAccess()
    },

    // ---- loading ----
    async load (quiet = false) {
      try {
        if (!quiet) this.loading = true
        const res = await axios.get(`${this.base}/reports`)
        this.reports = res.data.data.reports
        this.buildBoard(res.data.data.categories)
      } finally {
        this.loading = false
      }
    },
    buildBoard (categories) {
      const sorted = (list) => [...list].sort((a, b) => (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name))
      const board = categories.map((c) => ({ id: c.id, name: c.name, icon: c.icon, reports: sorted(this.reports.filter((r) => r.categoryId === c.id)) }))
      const loose = this.reports.filter((r) => !categories.some((c) => c.id === r.categoryId))
      if (loose.length) board.push({ id: 0, name: 'Uncategorized', reports: sorted(loose) })
      this.board = board
      this.selected = this.selected.filter((id) => this.reports.some((r) => r.id === id))
    },

    // ---- drag and drop: store category + order of the list that changed ----
    async onChange (cat, evt) {
      if (!evt.added && !evt.moved) return
      if (!cat.id) return
      try {
        await axios.put(`${this.base}/reports/arrange`, {
          items: cat.reports.map((r, i) => ({ id: r.id, categoryId: cat.id, sortOrder: (i + 1) * 10 }))
        })
        if (evt.added) this.$snack(`Moved "${evt.added.element.name}" to ${cat.name}.`, 'success')
      } finally {
        await this.load(true)
      }
    },

    // ---- bulk ----
    bulkCategory (cat, changes) { return this.bulkIds(cat.reports.map((r) => r.id), changes) },
    bulk (changes) { return this.bulkIds(this.selected, changes) },
    async bulkIds (ids, changes) {
      const res = await axios.post(`${this.base}/reports/bulk`, { ids, changes })
      const done = res.data.data
      const failed = done.filter((d) => d.status === 'error')
      const changed = done.filter((d) => d.status === 'updated').length
      this.$snack(failed.length ? `${changed} updated, ${failed.length} failed: ${failed[0].error}` : `${changed} report${changed === 1 ? '' : 's'} updated.`, failed.length ? 'warning' : 'success')
      await this.load(true)
    },
    openAccess () {
      this.access = { mode: 'replace', userTypeIds: [] }
      this.accessDialog = true
    },
    async applyAccess () {
      this.$set(this.busy, 'access', true)
      try {
        await this.bulkIds(this.selected, { permissions: { mode: this.access.mode, userTypeIds: this.access.userTypeIds } })
        this.accessDialog = false
      } finally {
        this.$set(this.busy, 'access', false)
      }
    },

    // ---- categories ----
    async openCategories () {
      const res = await axios.get(`${this.base}/reports`)
      this.cats = res.data.data.categories.map((c) => ({ ...c }))
      this.catDialog = true
    },
    async saveCat (c) {
      await axios.put(`${this.base}/categories/${c.id}`, { name: c.name, icon: c.icon, description: c.description })
      await this.load(true)
    },
    async saveCatOrder () {
      await axios.put(`${this.base}/reorder`, { items: this.cats.map((c, i) => ({ type: 'category', id: c.id, sortOrder: (i + 1) * 10 })) })
      await this.load(true)
    },
    async deleteCat (c) {
      await axios.delete(`${this.base}/categories/${c.id}`)
      this.cats = this.cats.filter((x) => x.id !== c.id)
      await this.load(true)
    },

    // ---- single report ----
    async withBusy (key, fn) {
      this.$set(this.busy, key, true)
      try { return await fn() } finally { this.$set(this.busy, key, false) }
    },
    async runTests (item) {
      await this.withBusy('t' + item.id, async () => {
        const res = await axios.post(`${this.base}/reports/${item.id}/test`, {})
        this.testResult = { name: item.name, data: res.data.data }
        this.testDialog = true
        await this.load(true)
      })
    },
    async duplicate (item) {
      const res = await axios.get(`${this.base}/reports/${item.id}`)
      const t = res.data.data.template
      t.key = `${t.key}-copy`
      t.name = `${t.name} (copy)`
      t.visibility = 'draft'
      const created = await axios.post(`${this.base}/reports`, { template: t, label: 'Duplicated' })
      this.$router.push(`/superadmin/report-builder/edit/${created.data.data.id}`)
    },
    async exportOne (item) {
      const res = await axios.get(`${this.base}/reports/${item.id}/export`)
      saveJson(`${item.key}.rbreport.json`, res.data.data)
    },
    async exportAll () {
      await this.withBusy('exportAll', async () => {
        const res = await axios.post(`${this.base}/export`, {})
        saveJson('reports.rbreport.json', res.data.data)
      })
    },
    async doImport () {
      await this.withBusy('import', async () => {
        const text = await this.importFile.text()
        let envelope
        try { envelope = JSON.parse(text) } catch (e) { this.$snack('That file is not valid JSON.', 'error'); return }
        const res = await axios.post(`${this.base}/import`, { envelope, strategy: this.importStrategy })
        this.importResults = res.data.data
        await this.load(true)
      })
    },
    async doDelete () {
      await this.withBusy('delete', async () => {
        await axios.delete(`${this.base}/reports/${this.confirmDelete.id}`)
        this.confirmDelete = null
        await this.load(true)
      })
    }
  }
}
</script>

<style scoped>
.rb-row { border-bottom: 1px solid rgba(128, 128, 128, 0.15); background: transparent; }
.rb-row-sel { background: rgba(25, 118, 210, 0.08); }
.rb-ghost { opacity: 0.4; background: rgba(25, 118, 210, 0.15); }
.rb-dropzone { min-height: 44px; }
</style>
